/**
 * @file apps/web/src/pages/RolesSettingsPage.tsx
 * @description Custom roles management settings page.
 * Lists all roles (system + custom). System roles are shown but locked.
 * Custom roles support editing name, description, and per-permission toggles.
 * Route: /settings/roles
 */

import { useEffect, useState, useCallback } from 'react';
import { Plus, Shield, Lock, Trash2, ChevronDown, ChevronUp, AlertCircle, Check, X } from 'lucide-react';
import { useAuth } from '../lib/auth.js';
import { rolesApi } from '../lib/api.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CustomRole {
  id:             string;
  workspace_id:   string;
  name:           string;
  description?:   string | null;
  permissions:    Record<string, boolean>;
  is_system_role: boolean;
  created_at:     string;
}

// ---------------------------------------------------------------------------
// Permission categories
// ---------------------------------------------------------------------------

const PERMISSION_CATEGORIES: { label: string; keys: { key: string; label: string }[] }[] = [
  {
    label: 'Test Scripts',
    keys: [
      { key: 'view_test_scripts',   label: 'View test scripts' },
      { key: 'edit_test_scripts',   label: 'Edit test scripts' },
      { key: 'delete_test_scripts', label: 'Delete test scripts' },
    ],
  },
  {
    label: 'FDD / Documents',
    keys: [
      { key: 'view_fdd',  label: 'View FDD' },
      { key: 'edit_fdd',  label: 'Edit FDD' },
      { key: 'share_fdd', label: 'Share FDD with client' },
    ],
  },
  {
    label: 'UAT',
    keys: [
      { key: 'view_uat',    label: 'View UAT' },
      { key: 'execute_uat', label: 'Execute UAT' },
      { key: 'manage_uat',  label: 'Manage UAT sessions' },
    ],
  },
  {
    label: 'Defects',
    keys: [
      { key: 'view_defects',    label: 'View defects' },
      { key: 'create_defects',  label: 'Log defects' },
      { key: 'resolve_defects', label: 'Resolve defects' },
    ],
  },
  {
    label: 'Approvals',
    keys: [
      { key: 'view_approvals',   label: 'View approvals' },
      { key: 'create_approvals', label: 'Create approval workflows' },
      { key: 'action_approvals', label: 'Approve / reject' },
    ],
  },
  {
    label: 'Client Portal',
    keys: [
      { key: 'view_client_portal',   label: 'Access client portal' },
      { key: 'manage_client_portal', label: 'Manage client portal' },
    ],
  },
  {
    label: 'Projects',
    keys: [
      { key: 'view_projects',    label: 'View projects' },
      { key: 'create_projects',  label: 'Create projects' },
      { key: 'archive_projects', label: 'Archive projects' },
    ],
  },
  {
    label: 'Administration',
    keys: [
      { key: 'manage_users',     label: 'Manage users' },
      { key: 'manage_roles',     label: 'Manage roles' },
      { key: 'view_audit_trail', label: 'View audit trail' },
      { key: 'trigger_signoff',  label: 'Trigger sign-off' },
    ],
  },
];

// ---------------------------------------------------------------------------
// PermissionMatrix
// ---------------------------------------------------------------------------

function PermissionMatrix({
  permissions,
  locked,
  onChange,
}: {
  permissions: Record<string, boolean>;
  locked:      boolean;
  onChange:    (key: string, value: boolean) => void;
}) {
  return (
    <div className="space-y-4">
      {PERMISSION_CATEGORIES.map((cat) => (
        <div key={cat.label}>
          <p className="text-[11px] font-mono font-semibold text-muted uppercase tracking-wider mb-2">
            {cat.label}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {cat.keys.map(({ key, label }) => {
              const enabled = permissions[key] ?? false;
              return (
                <label
                  key={key}
                  className={[
                    'flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors',
                    locked ? 'opacity-60 cursor-default' : '',
                    enabled
                      ? 'border-blue/30 bg-sky/40'
                      : 'border-[rgba(8,17,31,0.08)] bg-white hover:bg-sky/20',
                  ].join(' ')}
                >
                  <div
                    className={[
                      'w-4 h-4 rounded border-2 flex items-center justify-center shrink-0',
                      enabled
                        ? 'border-blue bg-blue'
                        : 'border-[rgba(8,17,31,0.2)] bg-white',
                    ].join(' ')}
                  >
                    {enabled && <Check size={10} className="text-white" />}
                  </div>
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={enabled}
                    disabled={locked}
                    onChange={(e) => !locked && onChange(key, e.target.checked)}
                  />
                  <span className="text-xs text-ink">{label}</span>
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RoleCard
// ---------------------------------------------------------------------------

function RoleCard({
  role,
  onDelete,
  onSave,
}: {
  role:     CustomRole;
  onDelete: (id: string) => void;
  onSave:   (id: string, name: string, description: string, permissions: Record<string, boolean>) => Promise<void>;
}) {
  const [expanded, setExpanded]       = useState(false);
  const [name, setName]               = useState(role.name);
  const [description, setDescription] = useState(role.description ?? '');
  const [permissions, setPermissions] = useState<Record<string, boolean>>(role.permissions);
  const [saving, setSaving]           = useState(false);
  const [err, setErr]                 = useState('');
  const [saved, setSaved]             = useState(false);

  const isDirty =
    name !== role.name ||
    description !== (role.description ?? '') ||
    JSON.stringify(permissions) !== JSON.stringify(role.permissions);

  const handleSave = async () => {
    setSaving(true);
    setErr('');
    try {
      await onSave(role.id, name, description, permissions);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const permCount  = Object.values(permissions).filter(Boolean).length;
  const totalPerms = PERMISSION_CATEGORIES.flatMap((c) => c.keys).length;

  return (
    <div className="card overflow-hidden">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left p-4 flex items-center justify-between gap-3 hover:bg-sky/10 transition-colors"
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {role.is_system_role
            ? <Lock size={14} className="text-muted shrink-0" />
            : <Shield size={14} className="text-blue shrink-0" />
          }
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-ink">{role.name}</p>
            {role.description && (
              <p className="text-[11px] font-mono text-muted mt-0.5 truncate">
                {role.description}
              </p>
            )}
          </div>
          <span className="text-[11px] font-mono text-muted shrink-0">
            {permCount}/{totalPerms} permissions
          </span>
          {role.is_system_role && (
            <span className="text-[10px] font-mono px-1.5 py-0.5 bg-gray text-muted rounded-full shrink-0">
              system
            </span>
          )}
        </div>
        {expanded ? <ChevronUp size={14} className="text-muted shrink-0" /> : <ChevronDown size={14} className="text-muted shrink-0" />}
      </button>

      {expanded && (
        <div className="border-t border-[rgba(8,17,31,0.06)] p-4 space-y-4">
          {!role.is_system_role && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label mb-1.5">Role Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input text-sm"
                />
              </div>
              <div>
                <label className="label mb-1.5">Description</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional description"
                  className="input text-sm"
                />
              </div>
            </div>
          )}

          <PermissionMatrix
            permissions={permissions}
            locked={role.is_system_role}
            onChange={(key, val) => setPermissions((p) => ({ ...p, [key]: val }))}
          />

          {err && (
            <div className="flex items-center gap-2 text-sm text-danger">
              <AlertCircle size={13} /> {err}
            </div>
          )}

          {!role.is_system_role && (
            <div className="flex items-center justify-between pt-2 border-t border-[rgba(8,17,31,0.06)]">
              <button
                onClick={() => onDelete(role.id)}
                className="btn-ghost text-sm text-danger hover:bg-danger/8"
              >
                <Trash2 size={13} /> Delete Role
              </button>
              <div className="flex items-center gap-2">
                {saved && (
                  <span className="text-xs text-success flex items-center gap-1">
                    <Check size={12} /> Saved
                  </span>
                )}
                <button
                  onClick={handleSave}
                  disabled={saving || !isDirty}
                  className="btn-primary text-sm"
                >
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// New role form
// ---------------------------------------------------------------------------

function NewRoleForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (name: string, description: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName]               = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving]           = useState(false);
  const [err, setErr]                 = useState('');

  const handleSubmit = async () => {
    if (!name.trim()) { setErr('Role name is required.'); return; }
    setSaving(true);
    setErr('');
    try {
      await onSubmit(name.trim(), description);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to create role');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-sm font-semibold text-ink">New Custom Role</h3>
        <button onClick={onCancel} className="btn-ghost text-xs text-muted">
          <X size={14} />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label mb-1.5">Role Name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Senior Project Team Member"
            className="input text-sm"
            autoFocus
          />
        </div>
        <div>
          <label className="label mb-1.5">Description</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional"
            className="input text-sm"
          />
        </div>
      </div>
      {err && (
        <div className="flex items-center gap-2 text-sm text-danger">
          <AlertCircle size={13} /> {err}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="btn-ghost text-sm">Cancel</button>
        <button onClick={() => void handleSubmit()} disabled={saving} className="btn-primary text-sm">
          {saving ? 'Creating…' : 'Create Role'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// RolesSettingsPage
// ---------------------------------------------------------------------------

export function RolesSettingsPage() {
  const { user, devHeaders } = useAuth();

  const [roles, setRoles]       = useState<CustomRole[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [showNew, setShowNew]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await rolesApi.list(user.tenantId, devHeaders) as CustomRole[];
      setRoles(data);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, [user.tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (name: string, description: string) => {
    const created = await rolesApi.create({
      workspaceId: user.tenantId,
      name,
      description: description || undefined,
      permissions: {},
    }, devHeaders) as CustomRole;
    setRoles((prev) => [...prev, created]);
    setShowNew(false);
  };

  const handleSave = async (
    id: string,
    name: string,
    description: string,
    permissions: Record<string, boolean>,
  ) => {
    const updated = await rolesApi.update(id, { name, description, permissions }, devHeaders) as CustomRole;
    setRoles((prev) => prev.map((r) => r.id === id ? updated : r));
  };

  const handleDelete = async (id: string) => {
    const role = roles.find((r) => r.id === id);
    if (!role) return;
    if (!confirm(`Delete role "${role.name}"? This cannot be undone.`)) return;
    await rolesApi.delete(id, devHeaders);
    setRoles((prev) => prev.filter((r) => r.id !== id));
  };

  const systemRoles = roles.filter((r) => r.is_system_role);
  const customRoles = roles.filter((r) => !r.is_system_role);

  return (
    <div className="p-8 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="page-title">Roles & Permissions</h1>
          <p className="text-sm text-muted mt-1">
            Define custom roles and control what each role can access.
          </p>
        </div>
        {!showNew && (
          <button onClick={() => setShowNew(true)} className="btn-primary text-sm">
            <Plus size={14} /> New Role
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm mb-4">
          <AlertCircle size={14} className="shrink-0" /> {error}
        </div>
      )}

      {showNew && (
        <div className="mb-4">
          <NewRoleForm
            onSubmit={handleCreate}
            onCancel={() => setShowNew(false)}
          />
        </div>
      )}

      {loading ? (
        <div className="space-y-2 animate-pulse">
          {[1,2,3,4].map((i) => <div key={i} className="card h-16 bg-gray/60" />)}
        </div>
      ) : (
        <div className="space-y-6">
          {/* Custom roles */}
          {customRoles.length > 0 && (
            <div>
              <p className="label mb-2">Custom Roles ({customRoles.length})</p>
              <div className="space-y-2">
                {customRoles.map((r) => (
                  <RoleCard
                    key={r.id}
                    role={r}
                    onDelete={handleDelete}
                    onSave={handleSave}
                  />
                ))}
              </div>
            </div>
          )}

          {/* System roles */}
          {systemRoles.length > 0 && (
            <div>
              <p className="label mb-2">System Roles (locked)</p>
              <div className="space-y-2">
                {systemRoles.map((r) => (
                  <RoleCard
                    key={r.id}
                    role={r}
                    onDelete={handleDelete}
                    onSave={handleSave}
                  />
                ))}
              </div>
            </div>
          )}

          {roles.length === 0 && (
            <div className="empty-state py-12">
              <Shield size={24} className="text-muted/40 mb-2 mx-auto" />
              <p className="text-sm text-muted">No roles defined yet.</p>
              <button onClick={() => setShowNew(true)} className="btn-secondary mt-4">
                <Plus size={14} /> Create First Role
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
